import { Tabs, TabsContent, TabsList, TabsTrigger, Button, Label } from '@creanizer/ui'
import type { ItemInput, ItemResult, ItemLockedSections } from '@creanizer/types'
import { Lock, Unlock, X } from 'lucide-react'
import { ItemStandardMode, ItemIdeaMode, ItemBatchMode } from './ItemFormModes'

interface ItemFormProps {
  input: ItemInput
  onChange: (input: ItemInput) => void
  onGenerate: () => void
  isGenerating: boolean
  partialRegenerateMode?: boolean
  existingResult?: ItemResult | null
  lockedSections?: ItemLockedSections
  onLockedSectionsChange?: (sections: ItemLockedSections) => void
  onPartialRegenerateModeChange?: (mode: boolean) => void
}

export function ItemForm({ 
  input, 
  onChange, 
  onGenerate, 
  isGenerating,
  partialRegenerateMode = false,
  existingResult = null,
  lockedSections = {},
  onLockedSectionsChange,
  onPartialRegenerateModeChange 
}: ItemFormProps) {
  // モード変更時のハンドラ - 他のモードの入力をクリア
  const handleModeChange = (newMode: string) => {
    const baseFields = {
      mode: newMode as ItemInput['mode'],
      rarity: input.rarity,
      genre: input.genre,
      customGenre: input.customGenre,
      worldContext: input.worldContext,
      powerLevel: input.powerLevel,
      techLevel: input.techLevel,
      creativityLevel: input.creativityLevel,
      useProMode: input.useProMode,
      additionalSettings: input.additionalSettings
    }

    if (newMode === 'standard') {
      // ideaとbatch特有のフィールドをクリア
      onChange({
        ...baseFields,
        name: input.name,
        itemType: input.itemType,
        customType: input.customType,
        category: input.category
      })
    } else if (newMode === 'idea') {
      // standardとbatch特有のフィールドをクリア
      onChange({
        ...baseFields,
        ideaText: input.ideaText,
        uploadedContent: input.uploadedContent
      })
    } else if (newMode === 'batch') {
      // standardとidea特有のフィールドをクリア
      onChange({
        ...baseFields,
        numberOfItems: input.numberOfItems || 5,
        batchTheme: input.batchTheme,
        batchPattern: input.batchPattern,
        rarityDistribution: input.rarityDistribution,
        sameRarity: input.sameRarity
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* 部分再生成モードの表示 */}
      {partialRegenerateMode && existingResult && (
        <div className="card bg-muted/50 border-2 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">部分再生成モード</h3>
              <span className="text-sm text-muted-foreground">
                「{existingResult.basicInfo.name}」を編集中
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (onLockedSectionsChange) {
                    // 変更不可の項目以外をすべてロック
                    onLockedSectionsChange({
                      basicInfo: {
                        name: true,
                        aliases: true,
                        type: true,
                        category: true,
                        rarity: true,
                        summary: true,
                        description: true,
                        detailedDescription: true,
                        significance: true
                      },
                      appearance: true,
                      effects: true,
                      acquisition: true,
                      lore: true,
                      gmNotes: true
                    })
                  }
                }}
              >
                一括ロック
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (onLockedSectionsChange) {
                    // すべてのロックを解除（変更不可の項目は除く）
                    onLockedSectionsChange({
                      basicInfo: {
                        name: true,
                        type: true,
                        rarity: true
                      }
                    })
                  }
                }}
              >
                一括解除
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (onPartialRegenerateModeChange) {
                    onPartialRegenerateModeChange(false)
                  }
                }}
              >
                <X className="h-3 w-3 mr-1" />
                部分再生成を終了
              </Button>
            </div>
          </div>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 gap-1 h-auto">
              <TabsTrigger value="basic" className="text-xs">基本</TabsTrigger>
              <TabsTrigger value="appearance" className="text-xs">外見</TabsTrigger>
              <TabsTrigger value="effects" className="text-xs">効果</TabsTrigger>
              <TabsTrigger value="acquisition" className="text-xs">入手</TabsTrigger>
              <TabsTrigger value="lore" className="text-xs">伝承</TabsTrigger>
            </TabsList>
            
            {/* 基本タブ */}
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  ※ 名前、タイプ、レアリティは変更できません
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'category', label: 'カテゴリ', locked: typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo?.category : lockedSections.basicInfo, immutable: false },
                    { key: 'summary', label: '概要', locked: typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo?.summary : lockedSections.basicInfo, immutable: false },
                    { key: 'description', label: '説明', locked: typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo?.description : lockedSections.basicInfo, immutable: false },
                    { key: 'detailedDescription', label: '詳細説明', locked: typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo?.detailedDescription : lockedSections.basicInfo, immutable: false },
                    { key: 'significance', label: '重要性', locked: typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo?.significance : lockedSections.basicInfo, immutable: false }
                  ].map(item => (
                    <Button
                      key={item.key}
                      variant={item.locked || item.immutable ? "default" : "outline"}
                      size="sm"
                      disabled={item.immutable}
                      onClick={() => {
                        if (!item.immutable && onLockedSectionsChange) {
                          onLockedSectionsChange({
                            ...lockedSections,
                            basicInfo: {
                              ...(typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo : {}),
                              name: true,
                              type: true,
                              rarity: true,
                              [item.key]: !item.locked
                            }
                          })
                        }
                      }}
                      className="justify-start"
                    >
                      {item.locked || item.immutable ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      {item.label}
                    </Button>
                  ))}
                </div>
                
                {/* 別名の個別ロック */}
                {existingResult?.basicInfo?.aliases && existingResult.basicInfo.aliases.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">別名</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {existingResult.basicInfo.aliases.map((alias, index) => {
                        const isLocked = typeof lockedSections.basicInfo === 'object' && lockedSections.basicInfo
                          ? Array.isArray(lockedSections.basicInfo.aliases)
                            ? lockedSections.basicInfo.aliases.includes(index)
                            : lockedSections.basicInfo.aliases === true
                          : false
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                let newAliases: boolean | number[]
                                if (typeof lockedSections.basicInfo === 'object' && lockedSections.basicInfo && Array.isArray(lockedSections.basicInfo.aliases)) {
                                  const current = lockedSections.basicInfo.aliases
                                  if (current.includes(index)) {
                                    newAliases = current.filter(i => i !== index)
                                  } else {
                                    newAliases = [...current, index]
                                  }
                                } else {
                                  newAliases = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  basicInfo: {
                                    ...(typeof lockedSections.basicInfo === 'object' ? lockedSections.basicInfo : {}),
                                    name: true,
                                    type: true,
                                    rarity: true,
                                    aliases: newAliases.length === 0 ? false : newAliases
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{alias}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* 外見タブ */}
            <TabsContent value="appearance" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.appearance === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          appearance: lockedSections.appearance === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.appearance === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    外見全体
                  </Button>
                </div>
                
                {/* 基本的な外見項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">基本的な外見</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'visualDescription', label: '見た目の説明' },
                      { key: 'material', label: '素材' },
                      { key: 'size', label: 'サイズ' },
                      { key: 'weight', label: '重量' },
                      { key: 'color', label: '色' },
                      { key: 'texture', label: '質感' },
                      { key: 'smell', label: '匂い' },
                      { key: 'sound', label: '音' },
                      { key: 'taste', label: '味' },
                      { key: 'condition', label: '状態' },
                      { key: 'age', label: '年代' },
                      { key: 'manufacturer', label: '製造者' },
                      { key: 'brand', label: 'ブランド' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.appearance === 'object' 
                        ? lockedSections.appearance[item.key as keyof typeof lockedSections.appearance]
                        : lockedSections.appearance === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentAppearance = typeof lockedSections.appearance === 'object' 
                                ? lockedSections.appearance 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                appearance: {
                                  ...currentAppearance,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* 効果タブ */}
            <TabsContent value="effects" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.effects === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          effects: lockedSections.effects === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.effects === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    効果全体
                  </Button>
                </div>
                
                {/* 基本的な効果項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">効果・性能</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'usageMethod', label: '使用方法' },
                      { key: 'activationCondition', label: '発動条件' },
                      { key: 'cooldown', label: 'クールダウン' },
                      { key: 'charges', label: 'チャージ数' },
                      { key: 'rechargeMethod', label: 'リチャージ方法' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.effects === 'object' 
                        ? lockedSections.effects[item.key as keyof typeof lockedSections.effects]
                        : lockedSections.effects === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentEffects = typeof lockedSections.effects === 'object' 
                                ? lockedSections.effects 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                effects: {
                                  ...currentEffects,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* 入手タブ */}
            <TabsContent value="acquisition" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.acquisition === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          acquisition: lockedSections.acquisition === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.acquisition === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    入手全体
                  </Button>
                </div>
                
                {/* 入手方法項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">入手・価値</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'dropRate', label: 'ドロップ率' },
                      { key: 'estimatedValue', label: '推定価値' },
                      { key: 'currency', label: '通貨' },
                      { key: 'valueFluctuation', label: '価値変動' },
                      { key: 'rarity', label: 'レア度' },
                      { key: 'demandLevel', label: '需要レベル' },
                      { key: 'availability', label: '入手可能性' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.acquisition === 'object' 
                        ? lockedSections.acquisition[item.key as keyof typeof lockedSections.acquisition]
                        : lockedSections.acquisition === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentAcquisition = typeof lockedSections.acquisition === 'object' 
                                ? lockedSections.acquisition 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                acquisition: {
                                  ...currentAcquisition,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* 伝承タブ */}
            <TabsContent value="lore" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.lore === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          lore: lockedSections.lore === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.lore === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    伝承全体
                  </Button>
                </div>
                
                {/* 伝承項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">伝承・設定</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'culturalImpact', label: '文化的影響' },
                      { key: 'symbolism', label: '象徴的意味' },
                      { key: 'flavorText', label: 'フレーバーテキスト' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.lore === 'object' 
                        ? lockedSections.lore[item.key as keyof typeof lockedSections.lore]
                        : lockedSections.lore === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentLore = typeof lockedSections.lore === 'object' 
                                ? lockedSections.lore 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                lore: {
                                  ...currentLore,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
      
      <Tabs 
      value={input.mode || 'standard'} 
      onValueChange={handleModeChange}
    >
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="standard">標準モード</TabsTrigger>
        <TabsTrigger value="idea">アイデアモード</TabsTrigger>
        <TabsTrigger value="batch">バッチモード</TabsTrigger>
      </TabsList>

      <TabsContent value="standard" className="space-y-4">
        <ItemStandardMode input={input} onChange={onChange} />
      </TabsContent>
      
      <TabsContent value="idea" className="space-y-4">
        <ItemIdeaMode
          input={input}
          onChange={onChange}
        />
      </TabsContent>
      
      <TabsContent value="batch" className="space-y-4">
        <ItemBatchMode input={input} onChange={onChange} />
      </TabsContent>
    </Tabs>
    </div>
  )
}